import { useState } from 'react';

interface VideoPlayerProps {
  clipId: string;
  videoUrl?: string;
}

/**
 * VideoPlayer component for inline video playback in tool results and markdown.
 * Used to display video clips extracted by the Video Analyst.
 * Expects a presigned S3 URL via the videoUrl prop.
 */
export function VideoPlayer({ clipId: _clipId, videoUrl: videoUrlProp }: VideoPlayerProps) {
  const [error, setError] = useState<string | null>(null);

  const videoUrl = videoUrlProp;
  if (!videoUrl) {
    return (
      <div className="bg-yellow-50 text-yellow-700 text-xs p-2 rounded border border-yellow-200">
        Video URL not available
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 text-xs p-2 rounded border border-red-200">
        Video unavailable: {error}
      </div>
    );
  }

  return (
    <div className="mb-3">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Video Clip
      </div>
      <div className="rounded-lg overflow-hidden border border-gray-200">
        <video
          src={videoUrl}
          className="w-full"
          controls
          onError={() => setError('Failed to load video')}
        />
      </div>
    </div>
  );
}

/**
 * Extract clip_id from tool result if this is a video extraction result.
 */
export function extractClipId(result: unknown): string | null {
  if (!result) return null;

  let parsed: unknown = result;

  // Parse if it's a string
  if (typeof result === 'string') {
    try {
      parsed = JSON.parse(result);
    } catch {
      return null;
    }
  }

  // Check for clip_id in the result
  if (typeof parsed === 'object' && parsed !== null) {
    const obj = parsed as Record<string, unknown>;
    if (obj.success === true && typeof obj.clip_id === 'string') {
      return obj.clip_id;
    }
  }

  return null;
}

/**
 * Extract video_url from tool result if this is a video extraction result with an S3 URL.
 */
export function extractVideoUrl(result: unknown): string | null {
  if (!result) return null;

  let parsed: unknown = result;

  if (typeof result === 'string') {
    try {
      parsed = JSON.parse(result);
    } catch {
      return null;
    }
  }

  if (typeof parsed === 'object' && parsed !== null) {
    const obj = parsed as Record<string, unknown>;
    if (obj.success === true && typeof obj.video_url === 'string') {
      return obj.video_url;
    }
  }

  return null;
}
